import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useCustomToast } from 'design-system';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { useParams } from 'react-router-dom';
import { useGetCategoriesApi } from './use-get-categories-api';
import { useGetCategoryDetailsApi } from './use-get-category-details-api';

const endpoint = 'api/v1/cms/catalog-categories';
export const createCategoryEndpointIdentifier = endpoint;

interface BodyType {
  names: {
    title: string;
    description?: string;
    language: string;
  }[];
  parentId: number;
  image: string;
  kasoId: string;
}

type TData = Record<string, any>;
type TError = AxiosError;

type TVariables = {
  body: BodyType;
};

type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

export function useCreateCategoryApi(options?: Options): ReturnType {
  const params = useParams<any>();
  const { refetch: refetchCategories } = useGetCategoriesApi({ enabled: false });
  const { refetch: refetchCategoryDetails } = useGetCategoryDetailsApi(params?.categoryId, { enabled: false });

  const toast = useCustomToast();

  const mutationFn = async ({ body }: TVariables) => {
    try {
      const { data } = await http.post(endpoint, body);

      // Refetch categories list
      refetchCategories();
      refetchCategoryDetails();

      toast({
        description: 'New category has been created successfully',
        status: 'success',
      });

      return data;
    } catch (error: any) {
      if (!error.response.status || !error.response.data.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, { mutationKey: endpoint, ...options });
}
